import {
  Iterator,
  IteratorProvider,
  IteratorResult
} from './Iterator';
import { preOrderTraversal } from './RoseTreeIterators';

/**
 * This class represents a generic rose tree implementation of a tree data structure.
 */
export class RoseTree<T> {
  // TODO make defensive copy of node and children
  public constructor(public readonly node: T, public readonly children: RoseTree<T>[]) { }

  /**
   * Smart constructor for creating a rose tree leaf.
   *
   * @param node - The node at the root of the leaf.
   */
  public static leaf<T>(node: T): RoseTree<T> {
    return new RoseTree(node, []);
  }

  /**
   * Checks if this tree is a leaf.
   */
  public get isLeaf(): boolean {
    return !this.children || this.children.length === 0;
  }

  /**
   * Computes the height of this tree.
   */
  public get height(): number {
    return this.isLeaf ? 0 : 1 + this.children.reduce((h, child) => Math.max(h, child.height), 0);
  }

  /**
   * Returns the number of nodes in this tree.
   */
  public get size(): number {
    return this.fold(sum => sum + 1, 0);
  }

  /**
   * Checks if this tree is equal to a given tree. Two trees are equal if they have the same shape and each of the corresponding nodes are
   * equal. Nodes are compared using strict equality.
   *
   * @param otherTree - The other tree to compare to this tree.
   */
  public equals({ node, children }: RoseTree<T>): boolean {
    if (this.node === node && this.children.length === children.length) {
      return this.children.every((child, i) => child.equals(children[i]));
    } else {
      return false;
    }
  }

  /**
   * Map operation for tree.
   *
   * @param f - The function to map over this tree.
   */
  public map<S>(f: (t: T) => S): RoseTree<S> {
    return new RoseTree(f(this.node), this.children.map(child => child.map(f)));
  }

  /**
   * Maps the tree to a new tree. Passes in each node's depth to the iteratee function.
   *
   * @param f - The function for mapping over this tree.
   * @param depth - The depth of the root node. Defaults to 0.
   */
  public mapWithDepth<S>(f: (t: T, d: number) => S, depth: number = 0): RoseTree<S> {
    return new RoseTree(f(this.node, depth), this.children.map(child => child.mapWithDepth(f, depth + 1)));
  }

  /**
   * Runs a function on every node in a tree. NOTE unlike `forEach` for arrays, this does not pass the node's index into the iteratee
   * function.
   *
   * @param tree - The tree to iterate over.
   * @param f - The function to be executed on each node.
   * @param ip - An iterator provider that defines the order of traversal. Uses pre-order traversal by default.
   */
  public forEach(f: (t: T) => void, ip: IteratorProvider<RoseTree<T>, T> = preOrderTraversal): void {
    const iterator: Iterator<T> = ip(this);
    let ir: IteratorResult<T> = iterator.next();
    while (!ir.done) {
      f(ir.value);
      ir = iterator.next();
    }
  }

  /**
   * Fold operation, i.e., reduce, for tree.
   *
   * @param f - The function to fold over this tree.
   * @param acc - The initial accumulator value.
   */
  public fold<S>(f: (s: S, t: T) => S, acc: S): S {
    return this.children.reduce((s, child) => child.fold(f, s), f(acc, this.node));
  }

  /**
   * Checks if some node in this tree satisfies the given predicate.
   *
   * @param p - The predicate.
   */
  public some(p: (t: T) => boolean): boolean {
    return this.fold((q, t) => q || p(t), false);
  }

  /**
   * Checks if all node in this tree satisfies the given predicate.
   *
   * @param p - The predicate.
   */
  public all(p: (t: T) => boolean): boolean {
    return this.fold((q, t) => q && p(t), true);
  }
}
